import { createStandardQueryClient } from "@plerity/data-access";
import { ErrorBoundary } from "@plerity/ui-core";
import { QueryClientProvider } from "@tanstack/react-query";
import React, { lazy, Suspense } from "react";
import ReactDOM from "react-dom/client";
import { createBrowserRouter, RouterProvider } from "react-router-dom";
import App from "./App";
import "./index.css";
import { initRuntimeReporter } from "@plerity/runtime-reporter";

// Phase 7: production runtime-error reporter — captures window.error +
// unhandledrejection and POSTs to /api/observability/errors.
// Disabled in dev (the test-time helper handles E2E specs).
initRuntimeReporter({
  app: "reviews",
  disabled: import.meta.env.MODE !== "production",
  buildSha: import.meta.env.VITE_BUILD_SHA ?? "dev",
});

/**
 * Register X-Tenant-Id interceptor on the shared dashboard API client.
 *
 * The Reviews app talks to the backend through TWO axios instances and BOTH
 * must carry the business switcher's `X-Tenant-Id` or the two diverge:
 *   1. `apiClient` (this file) — the dashboard-shared hooks (`useApiQuery`,
 *      `usePaginatedQuery`) used by every GET in `api/queries.ts`.
 *   2. the app-local `client` (`api/client.ts`, interceptor registered there)
 *      — used by every mutation in `api/mutations.ts`.
 *
 * Keep the two interceptors in sync. When only the shared client carried the
 * header, queries scoped to the selected business but mutations fell back to
 * the hostname-default org (`animas-marketing`) and 404'd for every client
 * business (Error KB reviews-mutation-tenant-header-divergence, 2026-07-24).
 */
import { apiClient as sharedApiClient } from "@plerity/dashboard-shared/hooks";
// Force Zod jitless under strict CSP — Zod v4's JIT probes `new Function("")`
// on first parse; the dashboards' CSP blocks it (caught, harmless) but the
// browser fires a securitypolicyviolation that floods the RUM pipeline
// (~2,800 `CSP: script-src blocked eval` occurrences as of 2026-07-15).
// Mirrors @plerity/contracts src/zod-config.ts (separate zod instance).
import { z as _zodCspConfig } from "zod";
import { useAdminStore } from "./lib/admin";

_zodCspConfig.config({ jitless: true });

sharedApiClient.interceptors.request.use((config) => {
  const selectedOrgId = useAdminStore.getState().selectedOrgId;
  if (selectedOrgId !== null) {
    config.headers.set("X-Tenant-Id", String(selectedOrgId));
  }
  return config;
});

const ReviewRequest = lazy(() => import("./routes/ReviewRequest"));

/**
 * Determine the app basename from the deployment context.
 *
 * - CF Pages (production): basename = "/"
 * - Local dev behind Vite proxy: basename = "/apps/reviews/"
 *
 * This must mirror the logic in App.tsx / vite.config.ts.
 */
declare const __CF_PAGES__: boolean;
const basename = typeof __CF_PAGES__ !== "undefined" && __CF_PAGES__ ? "/" : "/apps/reviews";

/**
 * Check if the current path is a customer-facing landing page.
 * The /r/:token path must be served without the dashboard shell (no auth, no sidebar).
 *
 * We check window.location here (before React renders) to avoid nested router issues.
 * AppShell (inside App) creates its own router, so we must not nest routers.
 */
const isLandingPage = /^(\/apps\/reviews)?\/r\/[^/]+/.test(window.location.pathname);

const rootEl = document.getElementById("root");
if (rootEl) {
  if (isLandingPage) {
    /**
     * Landing page path (/r/:token) — render standalone with its own router.
     * No dashboard shell, no sidebar, no auth.
     * Uses the data router API (createBrowserRouter + RouterProvider) to
     * comply with shell factory governance (no manual router outside createShell).
     */
    const landingQueryClient = createStandardQueryClient();

    const landingRouter = createBrowserRouter(
      [
        {
          path: "/r/:token",
          element: (
            <ErrorBoundary>
              <Suspense
                fallback={
                  <div className="min-h-screen flex items-center justify-center bg-surface-page">
                    <div className="h-8 w-8 rounded-full border-4 border-indigo-200 dark:border-indigo-800 border-t-indigo-600 dark:border-t-indigo-400 animate-spin" />
                  </div>
                }
              >
                <ReviewRequest />
              </Suspense>
            </ErrorBoundary>
          ),
        },
      ],
      { basename }
    );

    ReactDOM.createRoot(rootEl).render(
      <React.StrictMode>
        <QueryClientProvider client={landingQueryClient}>
          <RouterProvider router={landingRouter} />
        </QueryClientProvider>
      </React.StrictMode>
    );
  } else {
    /**
     * Dashboard path — render through the authenticated shell.
     * App internally uses createShell → AppShell → its own router.
     */
    ReactDOM.createRoot(rootEl).render(
      <React.StrictMode>
        <App />
      </React.StrictMode>
    );
  }
}
